import { AuthActions } from 'components/common/AuthActions'
import { Icon } from 'components/common/Icon'
import { Logo } from 'components/common/Logo'
import { MobileMenu } from 'components/common/MobileMenu'
import { TooltipMenu } from 'components/common/TooltipMenu'
import { ArtistsExtendedNav } from 'components/nav/ArtistsExtendedNav'
import { ArtistsMobileNav } from 'components/nav/ArtistsMobileNav'
import { ArtworksExtendedNav } from 'components/nav/ArtworksExtendedNav'
import { Nav } from 'components/nav/Nav'
import { NavItem } from 'components/nav/NavItem'
import { NotificationsPopup } from 'components/notifications/NotificationsPopup'
import { useFeatureFlags } from 'hooks'
import { useUserNotifications } from 'hooks/useUserNotifications'
import useTranslation from 'next-translate/useTranslation'
import { ReactNode, Suspense, lazy } from 'react'
import { saveScroll } from 'utils/scroll'
import routes from '../../constants/routes'
import { useAuth, useDrawer } from '../../contexts'

const SellPopupMenu = lazy(() =>
  import('components/common/SellPopupMenu').then(m => ({
    default: m.SellPopupMenu,
  }))
)
const MyAccountMobileNavMenu = lazy(() =>
  import('components/common/MyAccountPopupMenu').then(m => ({
    default: m.MyAccountMobileNavMenu,
  }))
)
const HIWPopupMenu = lazy(() =>
  import('components/common/HIWPopupMenu').then(m => ({
    default: m.HIWPopupMenu,
  }))
)
const HIWMobileNavMenu = lazy(() =>
  import('components/common/HIWPopupMenu').then(m => ({
    default: m.HIWMobileNavMenu,
  }))
)
const ArtworksMobileNav = lazy(() =>
  import('components/nav/ArtworksMobileNav').then(m => ({
    default: m.ArtworksMobileNav,
  }))
)

type Props = {
  className?: string
  vertical?: boolean
}

export const NavMenu = ({ vertical, className }: Props) => {
  const { isAuthenticated } = useAuth()
  const { t } = useTranslation('common')
  const { setConfig } = useDrawer()
  const {
    data: { notViewedCount = 0 },
  } = useUserNotifications()

  const { featureFlags } = useFeatureFlags()

  const labels = {
    home: t('home'),
    artworks: t('artworks'),
    artists: t('artists'),
    sell_an_item: t('sell_an_item'),
    how_it_works: t('how_it_works'),
    sell: t('sell'),
    buying: t('buying'),
    orders_and_bids: t('orders_and_bids'),
    selling: t('selling'),
    sales_and_asks: t('sales_and_asks'),
    settings: t('settings'),
    my_account: t('my_account'),
    calendar: t('calendar'),
    release_calendar: t('release_calendar'),
    notifications: t('notifications'),
  }

  const setMobileMenu = (title: string, body: ReactNode) => {
    setConfig({
      heading: (
        <div className='flex items-center space-x-[10px]'>
          <button
            type='button'
            onClick={() => {
              setConfig({
                heading: <Logo />,
                body: <MobileMenu actions={<AuthActions isMobile />} />,
              })
            }}
          >
            <Icon name='back-arrow' className='text-cool-500' size={20} />
          </button>
          <h5 className='font-body text-lg font-medium'>{title}</h5>
        </div>
      ),
      body,
      hasIcon: true,
    })
  }

  return (
    <Nav vertical={vertical} className={className} listClassName='!space-x-0'>
      <NavItem
        mobile
        href='/'
        exact
        className='lg:hidden'
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        {labels.home}
      </NavItem>

      <NavItem
        href={routes.artworks}
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
        className='hidden lg:block'
      >
        <ArtworksExtendedNav>{labels.artworks}</ArtworksExtendedNav>
      </NavItem>

      <NavItem
        mobile
        className='lg:hidden'
        onClick={() =>
          setMobileMenu(
            labels.artworks,
            <Suspense
              fallback={<div className='h-full w-full bg-cool-150'></div>}
            >
              <ArtworksMobileNav />
            </Suspense>
          )
        }
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        {labels.artworks}
      </NavItem>

      <NavItem
        className='hidden lg:block'
        href={routes.artists}
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        <ArtistsExtendedNav>{labels.artists}</ArtistsExtendedNav>
      </NavItem>

      <NavItem
        mobile
        className='lg:hidden'
        onClick={() =>
          setMobileMenu(
            labels.artists,
            <Suspense
              fallback={<div className='h-full w-full bg-cool-150'></div>}
            >
              <ArtistsMobileNav />
            </Suspense>
          )
        }
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        {labels.artists}
      </NavItem>

      <NavItem
        className='hidden lg:block'
        href={routes.releaseCalendar.base}
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        {labels.calendar}
      </NavItem>

      <NavItem
        className='lg:hidden'
        href={routes.releaseCalendar.base}
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        {labels.release_calendar}
      </NavItem>

      {featureFlags?.notification && isAuthenticated && (
        <NavItem
          mobile
          className='flex justify-between lg:hidden'
          href={routes.notifications}
          customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
        >
          <span>{labels.notifications}</span>

          {Boolean(notViewedCount) && (
            <div className='rounded-full bg-danger-dark px-2 py-0.5 text-xs font-medium text-cool-50'>
              {t('x_new', { count: notViewedCount })}
            </div>
          )}
        </NavItem>
      )}

      <NavItem
        mobile
        className='lg:hidden'
        href={routes.sell}
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        <span className='font-bold text-purple-500'>{labels.sell_an_item}</span>
      </NavItem>

      <NavItem
        mobile
        className='lg:hidden'
        onClick={() => {
          setMobileMenu(
            labels.how_it_works,
            <Suspense
              fallback={<div className='h-full w-full bg-cool-150'></div>}
            >
              <HIWMobileNavMenu />
            </Suspense>
          )
          saveScroll({ redirectedFrom: 'homepage' })
        }}
        customClick={() => saveScroll({ redirectedFrom: 'homepage' })}
      >
        {labels.how_it_works}
      </NavItem>

      <NavItem className='hidden lg:block'>
        <TooltipMenu
          content={
            <Suspense>
              <HIWPopupMenu />
            </Suspense>
          }
          contentClassName='mt-1 !z-[999999] max-w-[450px] '
          placement='bottom-left'
          className='flex cursor-pointer space-x-2'
        >
          {labels.how_it_works}
        </TooltipMenu>
      </NavItem>

      <NavItem theme='purple' className='hidden lg:block'>
        <TooltipMenu
          content={
            <Suspense
              fallback={<div className='h-full w-full bg-cool-150'></div>}
            >
              <SellPopupMenu />
            </Suspense>
          }
          placement='bottom-left'
          className='flex cursor-pointer space-x-2 '
          contentClassName='mt-1 max-w-[250px] !z-[999999]'
        >
          {labels.sell}
        </TooltipMenu>
      </NavItem>

      {featureFlags?.notification && isAuthenticated && (
        <div className='hidden pl-4 lg:block'>
          <NotificationsPopup
            onRedirect={() => saveScroll({ redirectedFrom: 'homepage' })}
          />
        </div>
      )}

      {isAuthenticated && (
        <NavItem
          mobile
          className='lg:hidden'
          onClick={() => {
            setMobileMenu(
              labels.my_account,
              <Suspense
                fallback={<div className='h-full w-full bg-cool-150'></div>}
              >
                <MyAccountMobileNavMenu />
              </Suspense>
            )
            saveScroll({ redirectedFrom: 'homepage' })
          }}
        >
          {labels.my_account}
        </NavItem>
      )}
    </Nav>
  )
}
